import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, MenuItem, Select, SelectChangeEvent, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, IconButton, Menu } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import Pagination from "../../components/Panigation";
import { USE_FAKE_DATA } from "../../config/appConfig";
import ActionDialog from "../../components/ActionDialog";

export type RFQStatus = "PENDING" | "APPROVED" | "REJECTED" | "NEEDS_INFO";

type RFQ = {
  id: string;
  customer: string;
  project: string;
  product: string;
  date: string; // ISO
  status: RFQStatus;
  quantity: number;
  grade: string;
  deliveryAddress: string;
  attachments?: string[];
};

const FAKE_RFQS: RFQ[] = [
  { id: "r1", customer: "Acme LLC", project: "Marina Tower", product: "ReadyMix M25", date: "2025-11-12", status: "PENDING", quantity: 120, grade: "M25", deliveryAddress: "Dubai Marina - Plot 12" },
  { id: "r2", customer: "BuildCo", project: "Expo Villas", product: "ReadyMix M30", date: "2025-11-11", status: "APPROVED", quantity: 200, grade: "M30", deliveryAddress: "Expo City - Villa 22" },
  { id: "r3", customer: "Skyline", project: "Harbour Point", product: "ReadyMix M20", date: "2025-11-10", status: "REJECTED", quantity: 80, grade: "M20", deliveryAddress: "Creek Harbour - Gate 3" },
  { id: "r4", customer: "Acme LLC", project: "Marina Tower", product: "ReadyMix M40", date: "2025-11-09", status: "NEEDS_INFO", quantity: 60, grade: "M40", deliveryAddress: "Dubai Marina - Plot 12" },
];

const RFQListPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<RFQStatus>("PENDING");
  const [searchTerm, setSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [customerType, setCustomerType] = useState("");

  const [items, setItems] = useState<RFQ[]>(USE_FAKE_DATA ? FAKE_RFQS : []);

  const [openReject, setOpenReject] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [editQty, setEditQty] = useState<number>(0);
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null);
  const [actionForId, setActionForId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let data = [...items];
    data = data.filter((r) => r.status === tab);
    const term = (debounced || "").toLowerCase();
    if (term) {
      data = data.filter(
        (r) =>
          r.customer.toLowerCase().includes(term) ||
          r.project.toLowerCase().includes(term) ||
          r.product.toLowerCase().includes(term)
      );
    }
    // placeholder for extra filters (e.g., customerType) if needed later
    return data;
  }, [items, tab, debounced]);

  const total = filtered.length;
  const start = (page - 1) * limit;
  const end = start + limit;
  const pageItems = filtered.slice(start, end);
  const totalPages = Math.max(1, Math.ceil(total / (limit || 10)));

  useEffect(() => {
    setPage(1);
  }, [tab, debounced]);

  const handleApprove = (id: string) => {
    if (!USE_FAKE_DATA) return;
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, status: "APPROVED" } : r)));
  };
  const handleReject = (id: string) => {
    setActiveId(id);
    setReason("");
    setOpenReject(true);
  };
  const handleEditScope = (id: string, qty: number) => {
    setActiveId(id);
    setEditQty(qty);
    setOpenEdit(true);
  };
  const handleRequestInfo = (id: string) => {
    setActiveId(id);
    setReason("");
    setOpenInfo(true);
  };

  const openActions = Boolean(actionAnchor);
  const handleOpenActions = (e: React.MouseEvent<HTMLElement>, id: string) => {
    setActionAnchor(e.currentTarget);
    setActionForId(id);
  };
  const handleCloseActions = () => {
    setActionAnchor(null);
    setActionForId(null);
  };

  const saveReject = () => {
    if (!USE_FAKE_DATA || !activeId) return;
    setItems((prev) => prev.map((r) => (r.id === activeId ? { ...r, status: "REJECTED" } : r)));
    setOpenReject(false);
  };
  const saveEdit = () => {
    if (!USE_FAKE_DATA || !activeId) return;
    setItems((prev) => prev.map((r) => (r.id === activeId ? { ...r, quantity: editQty } : r)));
    setOpenEdit(false);
  };
  const saveInfo = () => {
    if (!USE_FAKE_DATA || !activeId) return;
    setItems((prev) => prev.map((r) => (r.id === activeId ? { ...r, status: "NEEDS_INFO" } : r)));
    setOpenInfo(false);
  };

  return (
    <>
      <div className="main_title">
        <h1>RFQ Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - RFQs
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: "100%", mb: 1 }}>
              <Tabs
                value={tab === "PENDING" ? 0 : tab === "APPROVED" ? 1 : tab === "REJECTED" ? 2 : 3}
                onChange={(_, i) => setTab(i === 0 ? "PENDING" : i === 1 ? "APPROVED" : i === 2 ? "REJECTED" : "NEEDS_INFO")}
                className="custom_tabs_links"
              >
                <Tab label="Pending" />
                <Tab label="Approved" />
                <Tab label="Rejected" />
                <Tab label="Needs Info" />
              </Tabs>
            </Box>
            <SearchBar
              searchTerm={searchTerm}
              placeholder="Search by customer, project, product..."
              setDebouncedSearchTerm={setDebounced}
              value={searchTerm}
              onCross={() => setSearchTerm('')}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) setSearchTerm(val.target.value);
              }}
            />
          </div>
          <div className="right_s">
            <Button className="btn btn_primary" onClick={() => setItems(FAKE_RFQS)}>Reset</Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.length ? (
                pageItems.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.customer}</TableCell>
                    <TableCell>{r.project}</TableCell>
                    <TableCell>{r.product}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>{r.status.replace("_", " ")}</TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => handleOpenActions(e, r.id)}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={actionAnchor}
                        open={openActions && actionForId === r.id}
                        onClose={handleCloseActions}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <MenuItem onClick={() => { navigate(`/rfq/${r.id}`, { state: { rfq: r } }); handleCloseActions(); }}>View</MenuItem>
                        {tab !== "APPROVED" && (
                          <MenuItem onClick={() => { handleApprove(r.id); handleCloseActions(); }}>Approve</MenuItem>
                        )}
                        {tab !== "REJECTED" && (
                          <MenuItem onClick={() => { handleReject(r.id); handleCloseActions(); }}>Reject</MenuItem>
                        )}
                        <MenuItem onClick={() => { handleEditScope(r.id, r.quantity); handleCloseActions(); }}>Edit Scope</MenuItem>
                        <MenuItem onClick={() => { handleRequestInfo(r.id); handleCloseActions(); }}>Request More Info</MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>No RFQs found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Pagination module={pageItems} page={page} setPage={setPage} limit={limit} setLimit={setLimit} onPageChange={setPage} totalPages={totalPages} />
        </TableContainer>
      </div>

      {/* Reject Dialog */}
      <ActionDialog
        open={openReject}
        onClose={() => setOpenReject(false)}
        title="Reject RFQ"
        helperText="Provide a reason so the requester knows what to fix."
        confirmLabel="Reject"
        confirmDisabled={!reason.trim()}
        onConfirm={saveReject}
        confirmButtonProps={{ className: "btn btn_secondary" }}
      >
        <TextField
          label="Reason"
          placeholder="Enter rejection reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          sx={{
            '& textarea': {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              padding: '10px'
            }
          }}
        />
      </ActionDialog>

      {/* Edit Scope Dialog */}
      <ActionDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        title="Edit Scope"
        helperText="Adjust the required quantity before confirming."
        confirmLabel="Save"
        onConfirm={saveEdit}
        confirmButtonProps={{ className: "btn btn_primary" }}
      >
        <TextField
          type="number"
          label="Quantity"
          value={editQty}
          onChange={(e) => setEditQty(parseInt(e.target.value || '0', 10))}
          fullWidth
          size="small"
        />
      </ActionDialog>

      {/* Request More Info Dialog */}
      <ActionDialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title="Request More Info"
        helperText="Let the requester know what details are missing."
        confirmLabel="Send"
        confirmDisabled={!reason.trim()}
        onConfirm={saveInfo}
        confirmButtonProps={{ className: "btn btn_primary" }}
      >
        <TextField
          label="Message"
          placeholder="Ask for missing information"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          sx={{
            '& textarea': {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              padding: '10px'
            }
          }}
        />
      </ActionDialog>
    </>
  );
};

export default RFQListPage;
